import _ from "lodash";
import { nanoid } from "nanoid";
import { CourseValidationError, Skill } from "src/types/coaching/courses";
import { ObjectiveChangeRequest, Objectives, OrganizationChangeRequest, OrganizationInfo, Persona, PersonaChangeRequest, Simulation, SimulationChangeRequest, SimulationValidationError } from "src/types/coaching/simulation";

export type EntityType =  'discovery'| 'coldCall' | 'elevatorPitch';

export const prepareSimulationRequest = (source: Simulation, target: Simulation) => {
  const changes = {} as SimulationChangeRequest;

  if (!_.isEqual(source.name, target.name) && target.name) {
    changes.name = target.name;
  }

  if (!_.isEqual(source.description, target.description) && target.description) {
    changes.description = target.description;
  }

  if (!_.isEqual(source.banner, target.banner)) {
    changes.banner = { ...target.banner } as any;
    const sourceBackground = _.get(source, 'banner.background.id');
    const targetBackground = _.get(target, 'banner.background.id');

    if (sourceBackground !== targetBackground) {
      _.set(changes, 'banner.background', targetBackground);
    } else {
      _.set(changes, 'banner.background', null);
    }
  }

  if (!_.isEqual(source.duration, target.duration)) {
    changes.duration = target?.duration || 10;
  }

if (!_.isEqual(source.pitchScenario, target.pitchScenario)) {
  changes.pitchScenario = target.pitchScenario;
}

if (!_.isEqual(source.timeBound, target.timeBound)) {
  changes.timeBound = target.timeBound;
}

if (!_.isEqual(source.pitchAudience, target.pitchAudience)) {
  changes.pitchAudience = target.pitchAudience;
}

if (!_.isEqual(source.format, target.format)) {
  changes.format = target.format;
}


  if (!_.isEqual(source?.difficulty, target?.difficulty)) {
    changes.difficulty = target?.difficulty || 'easy';
  }

  if (!_.isEqual(source.assessmentConfigurationId, target.assessmentConfigurationId)) {
    changes.assessmentConfigurationId = target.assessmentConfigurationId;
  }

  if (!_.isEqual(source.language, target.language)) {
    changes.language = target.language;
  }

  const sourceSkills = (source.skills || []).map(item => item.id!);
  const targetSkills = (target.skills || []).map(item => item.id!);
  if (!_.isEqual(sourceSkills, targetSkills)) {
    changes.skills = targetSkills;
  }

  if (target.type !== 'elevator_pitch') {
  changes.persona = preparePersonaRequest(source.persona!, target.persona!);
  changes.organization = prepareOrganizationRequest(source.organization!, target.organization!);
  changes.objectives = prepareObjectivesRequest(source.objectives!, target.objectives!);
  }

  return changes;
}

export const preparePersonaRequest = (source: Persona | undefined, target: Persona | undefined) => {
  const changes = {} as PersonaChangeRequest;

  if (!_.isEqual(source?.name, target?.name)) {
    changes.name = target?.name;
  }

  if (!_.isEqual(source?.icon, target?.icon)) {
    changes.icon = target?.icon?.id ?? nanoid(6);
  }

  if (!_.isEqual(source?.description, target?.description)) {
    changes.description = target?.description;
  }

  if (!_.isEqual(source?.profile_summary, target?.profile_summary)) {
    changes.profile_summary = target?.profile_summary;
  }

  if (!_.isEqual(source?.designation, target?.designation)) {
    changes.designation = target?.designation;
  }

  if (!_.isEqual(source?.personality?.id, target?.personality?.id)) {
    changes.personality = target?.personality?.id;
  }

  if (!_.isEqual(source?.voice?.id, target?.voice?.id)) {
    changes.voice = target?.voice?.id;
  }

  if (!_.isEqual(source?.example_statements, target?.example_statements)) {
    changes.example_statements = target?.example_statements;
  }

  if (!_.isEqual(source?.objective_tendencies, target?.objective_tendencies)) {
    changes.objective_tendencies = target?.objective_tendencies;
  }

  if (!_.isEqual(source?.small_talk, target?.small_talk)) {
    changes.small_talk = target?.small_talk;
  }

  if (!_.isEqual(source?.success_metrics, target?.success_metrics)) {
    changes.success_metrics = target?.success_metrics;
  }

  return changes;
}

export const prepareOrganizationRequest = (source: OrganizationInfo | undefined, target: OrganizationInfo | undefined) => {
  const changes = {} as OrganizationChangeRequest;
  
  if (!_.isEqual(source?.name, target?.name)) {
    changes.name = target?.name;
  }

  if (!_.isEqual(source?.description, target?.description)) {
    changes.description = target?.description;
  }

  if (!_.isEqual(source?.summary, target?.summary)) {
    changes.summary = target?.summary;
  }

  if (!_.isEqual(source?.industry, target?.industry)) {
    changes.industry = target?.industry;
  }

  if (!_.isEqual(source?.revenue, target?.revenue)) {
    changes.revenue = target?.revenue;
  }

  if (!_.isEqual(source?.employees, target?.employees)) {
    changes.employees = target?.employees;
  }

  if (!_.isEqual(source?.tools_used, target?.tools_used)) {
    changes.tools_used = target?.tools_used;
  }

  return changes;
}

export const prepareObjectivesRequest = (source: Objectives | undefined, target: Objectives | undefined) => {
  const changes = {} as ObjectiveChangeRequest;

  if (!_.isEqual(source?.description, target?.description)) {
    changes.description = target?.description || "";
  }

  if (!_.isEqual(source?.goals, target?.goals)) {
    changes.goals = target?.goals || "";
  }

  if (!_.isEqual(source?.pain_points, target?.pain_points)) {
    changes.pain_points = target?.pain_points || "";
  }

  if (!_.isEqual(source?.deal_killers, target?.deal_killers)) {
    changes.deal_killers = target?.deal_killers || "";
  }

  if (!_.isEqual(source?.opportunity_triggers, target?.opportunity_triggers)) {
    changes.opportunity_triggers = target?.opportunity_triggers || "";
  }

  if (!_.isEqual(source?.assessment_validations, target?.assessment_validations)) {
    changes.assessment_validations = target?.assessment_validations || [];
  }

  return changes;
}

//Validate Simulation
export const validateSimulationRequest = (simulation: Simulation) => {
  const errors: SimulationValidationError[] = [];

  const pitchConfiguration = simulation.type === "elevator_pitch";

  if (!simulation.name || simulation.name.trim().length === 0) {
    errors.push({
      type: 'call-settings',
      uuid: simulation.id!,
      property: 'name',
      message: 'Name is required',
      warning: false
    });
  } else if (simulation.name.length > 200) {
    errors.push({
      type: 'call-settings',
      uuid: simulation.id!,
      property: 'name',
      message: 'Name cannot be more than 200 characters',
      warning: false
    });
  }

  if (!pitchConfiguration && !simulation.description) {
    errors.push({
      type: 'call-settings',
      uuid: simulation.id!,
      property: 'description',
      message: 'Descriptions are optional but highly recommended for completeness.',
      warning: true
    });
  }

  if (pitchConfiguration && !simulation.pitchScenario) {
    errors.push({
      type: 'call-settings',
      uuid: simulation.id!,
      property: 'pitchScenario',
      message: 'Scenario description is required to help learners understand the context of the pitch.',
      warning: true
    });
  }

  if (pitchConfiguration && !simulation.pitchAudience) {
    errors.push({
      type: 'call-settings',
      uuid: simulation.id!,
      property: 'pitchAudience',
      message: 'Audience description is required to help learners understand who they are pitching to.',
      warning: true
    });
  }

  if (_.size(simulation.skills) === 0) {
    errors.push({
      type: 'call-settings',
      uuid: simulation.id!,
      property: 'skills',
      message: 'Skills help learners to understand the simulation better. Please add at least one skill.',
      warning: false
    });
  }

  if (!pitchConfiguration) {

  //Organization

  if (_.size(simulation.organization?.industry) > 200) {
    errors.push({
      type: 'organization',
      uuid: simulation.id!,
      property: 'industry',
      message: 'Industry cannot be more than 200 characters',
      warning: false
    });
  }

  if (_.size(simulation.organization?.employees) > 200) {
    errors.push({
      type: 'organization',
      uuid: simulation.id!,
      property: 'employees',
      message: 'Employees cannot be more than 200 characters',
      warning: false
    });
  }

  if (_.size(simulation.organization?.revenue) > 200) {
    errors.push({
      type: 'organization',
      uuid: simulation.id!,
      property: 'revenue',
      message: 'Revenue cannot be more than 200 characters',
      warning: false
    });
  }

  if (_.size(simulation.organization?.revenue) > 200) {
    errors.push({
      type: 'organization',
      uuid: simulation.id!,
      property: 'revenue',
      message: 'Revenue cannot be more than 200 characters',
      warning: false
    });
  }

  if (_.size(simulation.organization?.tools_used) > 1000) {
    errors.push({
      type: 'organization',
      uuid: simulation.id!,
      property: 'tools_used',
      message: 'Tools used cannot be more than 1000 characters',
      warning: false
    });
  }

  //Persona
  if (_.size(simulation.persona?.name) === 0) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'name',
      message: 'Name is required',
      warning: false
    });
  } else if (_.size(simulation.persona?.name) > 200) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'name',
      message: 'Name cannot be more than 200 characters',
      warning: false
    });
  }

  if (_.size(simulation.persona?.description) === 0) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'description',
      message: 'Description is required',
      warning: false
    });
  } else if (_.size(simulation.persona?.description) > 1000) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'description',
      message: 'Description cannot be more than 1000 characters',
      warning: false
    });
  }

  if (_.size(simulation.persona?.profile_summary) === 0) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'description',
      message: 'Description is required',
      warning: true
    });
  } else if (_.size(simulation.persona?.profile_summary) > 1000) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'profile_summary',
      message: 'Profile summary cannot be more than 1000 characters',
      warning: false
    });
  }

  if (_.size(simulation.persona?.designation) > 200) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'designation',
      message: 'Designation cannot be more than 200 characters',
      warning: false
    });
  }

  if (_.size(simulation.persona?.objective_tendencies) > 1000) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'objective_tendencies',
      message: 'Objective tendencies cannot be more than 1000 characters',
      warning: false
    });
  }

  if (_.size(simulation.persona?.example_statements) > 1000) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'example_statements',
      message: 'Example statements cannot be more than 1000 characters',
      warning: false
    });
  }

  if (_.size(simulation.persona?.small_talk) > 1000) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'small_talk',
      message: 'Small talk cannot be more than 1000 characters',
      warning: false
    });
  }

  if (_.size(simulation.persona?.success_metrics) > 1000) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'success_metrics',
      message: 'Success metrics cannot be more than 1000 characters',
      warning: false
    });
  }

  if (!simulation.persona?.personality) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'personality',
      message: 'Personality is required',
      warning: false
    });
  }

  if (!simulation.persona?.voice) {
    errors.push({
      type: 'persona',
      uuid: simulation.id!,
      property: 'voice',
      message: 'Voice is required',
      warning: false
    });
  }

  //Objectives
  if (_.size(simulation.objectives?.description) === 0) {
    errors.push({
      type: 'objectives',
      uuid: simulation.id!,
      property: 'description',
      message: 'Description is required',
      warning: false
    });
  } else if (_.size(simulation.persona?.description) > 1000) {
    errors.push({
      type: 'objectives',
      uuid: simulation.id!,
      property: 'description',
      message: 'Description cannot be more than 1000 characters',
      warning: false
    });
  }

  if (_.size(simulation.objectives?.goals) === 0) {
    errors.push({
      type: 'objectives',
      uuid: simulation.id!,
      property: 'goals',
      message: 'Prospective objectives is required',
      warning: false
    });
  } else if (_.size(simulation.objectives?.goals) > 1000) {
    errors.push({
      type: 'objectives',
      uuid: simulation.id!,
      property: 'goals',
      message: 'Prospective objectives cannot be more than 1000 characters',
      warning: false
    });
  }

  if (_.size(simulation.objectives?.pain_points) > 1000) {
    errors.push({
      type: 'objectives',
      uuid: simulation.id!,
      property: 'pain_points',
      message: 'Company Pain Points cannot be more than 1000 characters',
      warning: false
    });
  }

  if (_.size(simulation.objectives?.deal_killers) > 1000) {
    errors.push({
      type: 'objectives',
      uuid: simulation.id!,
      property: 'deal_killers',
      message: 'Deal killers cannot be more than 1000 characters',
      warning: false
    });
  }

  if (_.size(simulation.objectives?.opportunity_triggers) > 1000) {
    errors.push({
      type: 'objectives',
      uuid: simulation.id!,
      property: 'opportunity_triggers',
      message: 'Opportunity triggers cannot be more than 1000 characters',
      warning: false
    });
  }
}

  return errors;
}

export const processSkills = (skills: Skill[], selected: Skill[]) => {
  let changes = [...selected];

  const parentMap = new Map();
  selected.map(item => {
    if (item.parent) {
      let children = parentMap.get(item.parent);
      if (!children) {
        children = [];
        parentMap.set(item.parent, children);
      }

      children = [...children, item.id]
      parentMap.set(item.parent, children);
    }
  });

  parentMap.forEach((children, parent) => {
    const skill = skills.find(item => item.id === parent);
    if (skill && _.size(skill.children) > 0) {
      const filtered = skill.children.filter(child => !_.includes(children, child.id));

      if (_.size(filtered) == 0) {
        changes = changes.filter(item => !_.includes(children, item.id))
        changes = [...changes, skill];
      }
    }
  });

  return changes;
}

export const getCallType = (type?: string): EntityType | undefined => {
  if (!type) return undefined;

  switch (type) {
    case 'cold_call':
      return 'coldCall';

    case 'elevator_pitch':
      return 'elevatorPitch';

    case 'discovery':
      return 'discovery';

    default:
      return undefined;
  }
};